import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  useMediaQuery,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ButtonConsensus, HeaderModal } from 'components';
import { colors } from 'utils/colorsSteps';
import ModalConfirm from 'components/ModalConfirm';
import intimationService from 'services/intimation/intimationService';
import inquiryService from 'services/inquiries/inquiryService';
import { toast } from 'react-toastify';

const ModalGenerateAndMonitorNoticePublic = props => {
  const { open, onClose, inquiry, intimation, onRefresh, getIntimations } = props;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [noticeUrl, setNoticeUrl] = useState(null);
  const [hasExistingNotice, setHasExistingNotice] = useState(false);

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const statusThatNoticeHasBeenPublished = [
    'MANUAL_PUBLISHED_NOTICE', 
    'PUBLISHED_NOTICE'
  ];

  useEffect(() => {
    if (open) {
      setNoticeUrl(null);
      setOpenModalConfirm(false);
      setLoadingGenerate(false);
      setHasExistingNotice(false);
      checkForExistingNotice();
    }
  }, [open]);

  const checkForExistingNotice = async () => {
    if (!inquiry?.inquiry?.id || !intimation?.id) {
      setHasExistingNotice(false);
      return;
    }

    try {
      setLoadingFiles(true);
      const response = await inquiryService.getFilesInquiryService(inquiry.inquiry.id);
      const files = response.data.data.length ? response.data.data : [];

      let recipientSegment = '';

      if (intimation.recipient === 'Vítima') {
        recipientSegment = `victim/${intimation.victim?.id}`;
      } else {
        recipientSegment = `inquiryInvestigated/${inquiry.id}`;
      }

      const existingFile = files.find(file => {
        const isIntimationNoticeType = file.fileType?.id === 10;
        const pathMatches = file.path?.startsWith(
          `intimations/${intimation.id}/${recipientSegment}/`,
        );

        return isIntimationNoticeType && pathMatches;
      });

      if (existingFile) {
        setNoticeUrl(existingFile.url);
        setHasExistingNotice(true);
      } else {
        setHasExistingNotice(false);
      }
    } catch (error) {
      setHasExistingNotice(false);
      toast.error('Erro ao buscar edital.');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleGenerateClick = () => {
    setOpenModalConfirm(true);
  };

  const handleConfirmGenerate = async () => {
    try {
      setLoadingGenerate(true);
      const response = await intimationService.getIntimationNotice(intimation.id);

      if (response.data) {
        await checkForExistingNotice();
      }
    } catch (error) {
      toast.error('Erro ao gerar edital.');
    } finally {
      setLoadingGenerate(false);
      setOpenModalConfirm(false);
    }
  };

  const handleDownload = () => {
    if (noticeUrl) {
      window.open(noticeUrl, '_blank');
    }
  };

  const handleConfirmPublish = () => {
    try {
      intimationService.confirmIntimationNoticePublish(intimation.id).then(() => {
        toast.success('Publicação do edital confirmada com sucesso.');
        if (getIntimations) {
          getIntimations();
        }
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      });
    } catch (error) {
      toast.error('Erro ao confirmar publicação do edital.');
    }
  };

  return (
    <>
      <Dialog fullScreen={fullScreen} maxWidth="xs" fullWidth open={open} onClose={onClose}>
        <HeaderModal onClose={onClose}>Criação / Download do Edital</HeaderModal>

        {loadingFiles ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginLeft: 8 }}>
              Carregando arquivos...
            </Typography>
          </Box>
        ) : (
          <>
            {intimation?.noticeStatus && (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {intimation.noticeStatus === 'AWAITING_NOTICE' &&
                    'Situação: Aguardando publicação do edital'}
                  {statusThatNoticeHasBeenPublished.includes(intimation.noticeStatus) &&
                    'Situação: O edital foi publicado'}
                </Typography>
              </Box>
            )}

            <DialogActions style={{ justifyContent: 'center', padding: 15, gap: 8 }}>
              {hasExistingNotice ? (
                <ButtonConsensus
                  title="Baixar edital"
                  backgroundColor={colors.primary}
                  hovercolor={colors.primary}
                  color="#fff"
                  onClick={handleDownload}
                  disabled={!noticeUrl}
                  style={{ minWidth: '120px', fontSize: '0.875rem' }}
                />
              ) : (
                <ButtonConsensus
                  title="Gerar edital"
                  backgroundColor={colors.primary}
                  hovercolor={colors.primary}
                  color="#fff"
                  onClick={handleGenerateClick}
                  style={{ minWidth: '120px', fontSize: '0.875rem' }}
                />
              )}

              {hasExistingNotice && intimation?.noticeStatus === 'AWAITING_NOTICE' && (
                <ButtonConsensus
                  title="Informar Publicação"
                  backgroundColor={colors.secondary || '#6c757d'}
                  hovercolor={colors.secondary || '#5a6268'}
                  color="#fff"
                  onClick={handleConfirmPublish}
                  style={{ minWidth: '140px', fontSize: '0.875rem' }}
                />
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {openModalConfirm && (
        <ModalConfirm
          title="Confirmação"
          open={openModalConfirm}
          handleClose={() => setOpenModalConfirm(false)}
          onClick={handleConfirmGenerate}
          disabledConfirm={loadingGenerate}
          noCloseOnConfirm={true}
        >
          <Typography variant="h5">Deseja gerar o edital?</Typography>
        </ModalConfirm>
      )}
    </>
  );
};

export default ModalGenerateAndMonitorNoticePublic;
